import { useEffect, useState } from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatTimestamp, formatTimestampRelativeLong } from '@/lib/date-utils'

// 1 minute — the coarsest bucket `formatTimestampRelativeLong` renders is
// minutes, so re-rendering more often than that wouldn't change the text.
const TICK_INTERVAL_MS = 60_000

interface ReceivedTimeLabelProps {
  createdAt?: unknown
  className?: string
}

/**
 * "Received 45 minutes ago" for the job detail page's Lead segment,
 * derived from the inquiry's `createdAt` (falling back to `job.createdAt`
 * when there is no inquiry) since backfilled jobs have `createdAt` set to
 * their migration time rather than the lead's actual received time.
 * Re-renders on an interval so the relative text stays current, and exposes
 * the exact local timestamp via a hover/focus tooltip.
 */
export function ReceivedTimeLabel({ createdAt, className }: ReceivedTimeLabelProps) {
  const [, setTick] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), TICK_INTERVAL_MS)
    return () => clearInterval(id)
  }, [])

  if (!createdAt) return null

  const relative = formatTimestampRelativeLong(createdAt)
  if (!relative) return null

  const exact = formatTimestamp(createdAt, "MMM d, yyyy 'at' h:mm a")

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'border-0 bg-transparent p-0 text-left font-mono text-xs text-muted-foreground',
            className
          )}
        >
          Received {relative}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">Received {exact}</TooltipContent>
    </Tooltip>
  )
}
